import React, { Fragment, useState, useContext, useEffect } from "react";
import { ErrorContext } from "../reducer/errorReducer";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import CircularProgress from "@material-ui/core/CircularProgress";
import CardMedia from "@material-ui/core/CardMedia";

import { processRedirect } from "../lib/redirectLib";
import { getExtendedUserDataAsync } from "../lib/userLib";
import HatchHeaderLogo from "../components/HatchHeaderLogo";
import isAuthorized from "../components/IsAuthorized";
import LogoFooter from "../components/LogoFooter";
import ProfileInfo from "../components/ProfileInfo";
import WilcomIdAppBar from "../components/WilcomIdAppBar";
import useSignInStatus from "../hooks/useSignInStatus";
import wilcomStyles from "../theme/WilcomStyles";
import { Button, Grid, TextField, Typography } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import corelDrawLogo from "../media/icons/corel-draw-icon.png";

const SUPPORTED_COREL_DRAW_KEYS = process.env.REACT_APP_SUPPORTED_COREL_DRAW_KEYS
  ? JSON.parse(process.env.REACT_APP_SUPPORTED_COREL_DRAW_KEYS)
  : [
      {
        corelKeyField: "corelKey2025",
        year: "2025",
        downloadLink: process.env.REACT_APP_COREL_DRAW_2025_DOWNLOAD_LINK,
        downloadText: "Download CorelDRAW® 2025",
      },
      {
        corelKeyField: "corelKey2024",
        year: "2024",
        downloadLink: process.env.REACT_APP_COREL_DRAW_2024_DOWNLOAD_LINK,
        downloadText: "Download CorelDRAW® 2024",
      },
      {
        corelKeyField: "corelKey",
        year: "2021",
        downloadLink: process.env.REACT_APP_COREL_DRAW_DOWNLOAD_LINK,
        downloadText: "Download CorelDRAW® 2021",
      },
    ];

const HATCH_DOWNLOADS = [
  {
    downloadLink: process.env.REACT_APP_HATCH4_DOWNLOAD_LINK,
    downloadText: "Download Hatch 4",
    customerGroupMatch: /(WHO|WHD|WHP|WHS)04/,
  },
  {
    downloadLink: process.env.REACT_APP_HATCH3_DOWNLOAD_LINK,
    downloadText: "Download Hatch 3",
    customerGroupMatch: /(WHO|WHD|WHP|WHS)03/,
  },
];

const useStyles = makeStyles(wilcomStyles);

export default function Home(props) {
  const classes = useStyles();
  const [extendedUserData, setExtendedUserData] = useState("");
  const [extendedUserDataIsLoading, setExtendedUserDataIsLoading] = useState(false);
  const [hatchDownloads, setHatchDownloads] = useState([]);
  const { isSignedIn, userName, userData } = useSignInStatus();
  const { dispatch, showMessage } = useContext(ErrorContext);
  useEffect(() => {
    return () => {
      if (window.timer) clearTimeout(window.timer);
      dispatch({ type: "hide" });
    };
  }, [dispatch]);
  //test: http://localhost:3000/?return_url=http://www.abc.net.au
  processRedirect();

  function updateUserData() {
    console.log(`Updating ${userName}`);
    setExtendedUserDataIsLoading(true);
    getExtendedUserDataAsync(userName)
      .then((extendedData) => {
        console.log({ extendedData });
        if (!!extendedData && extendedData.country) {
          //user hasn't completed sign in
          setExtendedUserData(extendedData);
          // Determine which Hatch versions the user can re-download
          const customerGroupParts = extendedData?.customer_group?.split("-");
          console.log({ customerGroupParts });

          if (!customerGroupParts) {
            // Don't show download buttons if customer group format is invalid
            console.error("Invalid customer group format:", extendedData?.customer_group);
            return;
          }

          const isTrial = /^(TA|TX)/.test(customerGroupParts?.[0]);
          const showHatchDownloads = HATCH_DOWNLOADS.filter(
            ({ customerGroupMatch }) => customerGroupParts?.[1]?.match(customerGroupMatch) || isTrial
          );
          setHatchDownloads(showHatchDownloads);
        } else {
          showMessage("signing out...", "success");

          window.location = "/signout";
        }
      })
      .catch((err) => showMessage(err.message || "An unexpected error occurred. Please try again.", "error"))
      .finally((data) => setExtendedUserDataIsLoading(false));
  }
  function onProfileUpdateComplete() {
    showMessage(`Update successful!`, "success");
  }
  function onProfileUpdateNotify(notification) {
    showMessage(notification, "error");
  }

  function copyToClipboard(value) {
    navigator.clipboard.writeText(value).catch(() => {
      showMessage("Failed to copy to clipboard", "error");
    });
  }

  if (!!userName && !extendedUserData && !extendedUserDataIsLoading) {
    updateUserData();
  }

  const authorizedComponent = (
    <div>
      <WilcomIdAppBar isSignedIn={isSignedIn} userName={userName} />
      <Container component="main" className={classes.mainContainer} maxWidth="sm">
        <CssBaseline />
        <div className={classes.paper}>
          <HatchHeaderLogo />
          <br />
          {!extendedUserData ? (
            <Fragment>
              <br />
              <CircularProgress variant="indeterminate" size={36} thickness={4} />
            </Fragment>
          ) : (
            <ProfileInfo
              userName={userName}
              userData={userData}
              extendedUserData={extendedUserData}
              onComplete={onProfileUpdateComplete}
              onNotify={onProfileUpdateNotify}
              onFinally={updateUserData}
            />
          )}
        </div>
      </Container>

      {SUPPORTED_COREL_DRAW_KEYS?.some(({ corelKeyField }) => !!extendedUserData[corelKeyField]) && (
        <Container component="main" className={classes.mainContainer} maxWidth="sm" style={{ marginTop: "1rem" }}>
          {SUPPORTED_COREL_DRAW_KEYS.filter(({ corelKeyField }) => !!extendedUserData[corelKeyField]).map(
            ({ corelKeyField, year, downloadLink, downloadText }, index) => (
              <div key={corelKeyField} style={index > 0 ? { marginTop: "2rem" } : undefined}>
                <Grid>
                  <div className={classes.corelDrawTitleWrapper}>
                    <CardMedia className={classes.corelDrawLogo} image={corelDrawLogo} title="CorelDRAW®" />
                    <Typography className={classes.corelDrawTitle}>
                      Your CorelDRAW® Standard {year} Product Key
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} style={{ display: "flex", gap: 10 }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    label="CorelDraw Product Key"
                    value={extendedUserData[corelKeyField]}
                    InputProps={{
                      classes: {
                        input: classes.corelKeyInput,
                      },
                    }}
                    disabled
                  />
                  <Button
                    aria-label="Copy CorelDraw Product Key"
                    onClick={() => copyToClipboard(extendedUserData[corelKeyField])}
                  >
                    <FileCopyIcon />
                  </Button>
                </Grid>
                <Grid>
                  <Typography>
                    <a href={downloadLink} className={classes.downloadCorelDrawLink}>
                      {downloadText}
                    </a>
                  </Typography>
                </Grid>
              </div>
            )
          )}
        </Container>
      )}
      {hatchDownloads.length > 0 && (
        <Container
          component="main"
          className={classes.mainContainer}
          maxWidth="sm"
          style={{ paddingTop: "1rem", marginTop: "0", backgroundColor: "unset", border: "none" }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography align="left" variant={"subtitle1"} className={classes.changePasswordHeading}>
                <b>Re-download Hatch</b>
              </Typography>
            </Grid>
            {hatchDownloads.map(({ downloadLink, downloadText }) => (
              <Grid item xs={12} key={downloadText}>
                <Button href={downloadLink} fullWidth variant="outlined" color="secondary">
                  {downloadText}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}
      <LogoFooter />
    </div>
  );

  return isAuthorized({ authorizedComponent, props });
}
